import { forwardRef } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { DocProcessingErrorType } from '../../../../../../../../../../backend/src/types/types';
import { Icon } from '../../../../../../../components/icon';
import { Doc } from '@/api/types/backend-dto-types';
import { cn } from '@/helpers/shadCnUtils';
import { ProcessingChip, getProcessingState, ProcessingState } from '@/new/components/processing-chip';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/new/components/shadcn/tooltip';

export const DocRow = forwardRef<
  HTMLDivElement,
  {
    doc: Doc & {
      hasKeyFacts: boolean;
      hasRelevantFacts: boolean;
      chunkMatchCount: number;
    };
    subsetId: string;
    searchQuery?: string;
    isSelected?: boolean;
    onSelect?: () => void;
  }
>(({ doc, subsetId, searchQuery, isSelected = false, onSelect }, ref) => {
  const { matterId } = useParams();
  const navigate = useNavigate();

  const processingState = getProcessingState(doc);

  return (
    <div
      ref={ref}
      className={cn(
        'group relative cursor-pointer scroll-my-8 rounded-md border border-gray-300 bg-white shadow-lg/2 transition-all duration-200 hover:shadow-lg/8 hover:shadow-teal-500',

        isSelected
          ? processingState === ProcessingState.NotProcessed
            ? 'border-red-300'
            : 'border-teal-400'
          : processingState === ProcessingState.NotProcessed
            ? 'border-red-300'
            : 'border-gray-300',
      )}
      onClick={onSelect}
    >
      <div
        className={cn(
          'flex items-center justify-between rounded-t-md border-b p-4 py-1.75 pr-1.5',
          processingState === ProcessingState.NotProcessed ? 'border-red-200 bg-red-50' : 'bg-gray-50',
        )}
      >
        <span className="flex items-center gap-2">
          <Icon
            className={cn(
              'lucide--file shrink-0 text-xs text-gray-400 group-hover:text-teal-500',
              isSelected && 'text-teal-500',
              processingState === ProcessingState.NotProcessed && 'text-red-400 group-hover:text-red-500',
            )}
          />
          <h3 className="line-clamp-1 text-xs font-medium text-ellipsis text-gray-900/80" title={doc.file.name}>
            {doc.file.name}
          </h3>
          {doc.file.relativityMetadata && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center rounded bg-purple-100 px-1.5 py-0.5">
                    <Icon className="lucide--database h-3 w-3 text-purple-600" />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-xs">Relativity Metadata Available</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {searchQuery && doc.chunkMatchCount > 0 && (
            <div
              className="mr-1.5 flex cursor-pointer items-center rounded bg-teal-100 px-1.5 py-0.5 transition-colors hover:bg-teal-200"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  `/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}?searchQuery=${encodeURIComponent(searchQuery)}`,
                );
              }}
            >
              <span className="text-[0.65rem] font-medium whitespace-nowrap text-teal-700">
                {doc.chunkMatchCount} {doc.chunkMatchCount === 1 ? 'match' : 'matches'} in content
              </span>
            </div>
          )}
        </span>
        <div className="flex items-center gap-2">
          {processingState !== ProcessingState.Processed && (
            <ProcessingChip processingState={getProcessingState(doc)} />
          )}
          <Button
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              navigate(`/matters/${matterId}/subsets/${subsetId}/docs/${doc.id}`);
            }}
          >
            View
            <Icon className="iconify lucide--arrow-right" />
          </Button>
        </div>
      </div>

      <div className="p-4 py-2 pb-2.5 text-xs">
        <div className="line-clamp-1">
          <span className="pr-1 font-medium text-gray-800/60">Author:</span>
          {doc.author ? (
            <span className="text-gray-900/80" title={doc.author}>
              {doc.author}
            </span>
          ) : (
            <span className="font-medium text-gray-500">N/A</span>
          )}
        </div>

        <div className="line-clamp-1">
          <span className="pr-1 font-medium text-gray-800/60">Date:</span>
          {doc.dateText ? (
            <span className="text-gray-900/80">{doc.dateText}</span>
          ) : (
            <span className="font-medium text-gray-500">N/A</span>
          )}
        </div>
      </div>
    </div>
  );
});

DocRow.displayName = 'DocRow';
